"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SpectraTable = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const get_1 = __importDefault(require("lodash/get"));
const react_1 = require("react");
const fa_1 = require("react-icons/fa");
const io5_1 = require("react-icons/io5");
const ui_1 = require("react-science/ui");
const clipboardComponents_1 = require("../../../utils/clipboard/clipboardComponents");
const clipboardHooks_1 = require("../../../utils/clipboard/clipboardHooks");
const DispatchContext_1 = require("../../context/DispatchContext");
const ReactTable_1 = __importDefault(require("../../elements/ReactTable/ReactTable"));
const Alert_1 = require("../../elements/popup/Alert");
const usePanelPreferences_1 = require("../../hooks/usePanelPreferences");
const ExportAsJcampModal_1 = __importDefault(require("../../modal/ExportAsJcampModal"));
const ColorIndicator_1 = __importDefault(require("./base/ColorIndicator"));
const ShowHideSpectrumButton_1 = __importDefault(require("./base/ShowHideSpectrumButton"));
const SpectrumName_1 = require("./base/SpectrumName");
function formatValueAsHTML(value) {
    if (value) {
        value = value.replaceAll(/(?<value>\d+)/g, '<sub>$<value></sub>');
    }
    return value;
}
function getActiveSpectraAsObject(activeSpectra) {
    const result = {};
    if (activeSpectra) {
        for (const activeSpectrum of activeSpectra) {
            result[activeSpectrum.id] = true;
        }
    }
    return result;
}
const columnStyle = {
    maxWidth: 0,
    overflow: 'hidden',
};
const options = [
    {
        label: 'Recolor based on distinct value',
        type: 'option',
        icon: (0, jsx_runtime_1.jsx)(io5_1.IoColorPaletteOutline, {}),
    },
];
var SpectraContextMenuOptionsKeys;
(function (SpectraContextMenuOptionsKeys) {
    SpectraContextMenuOptionsKeys["CopyToClipboard"] = "CopyToClipboard";
    SpectraContextMenuOptionsKeys["Delete"] = "Delete";
    SpectraContextMenuOptionsKeys["ExportAsJcamp"] = "ExportAsJcamp";
})(SpectraContextMenuOptionsKeys || (SpectraContextMenuOptionsKeys = {}));
const Spectra2DContextMenuOptions = [
    {
        label: 'Copy to Clipboard',
        type: 'option',
        icon: (0, jsx_runtime_1.jsx)(fa_1.FaCopy, {}),
        data: { id: SpectraContextMenuOptionsKeys.CopyToClipboard },
    },
    {
        label: 'Delete',
        type: 'option',
        icon: (0, jsx_runtime_1.jsx)(fa_1.FaRegTrashAlt, {}),
        data: { id: SpectraContextMenuOptionsKeys.Delete },
    },
    {
        label: 'Export as jcamp',
        type: 'option',
        icon: (0, jsx_runtime_1.jsx)(fa_1.FaFileExport, {}),
        data: { id: SpectraContextMenuOptionsKeys.ExportAsJcamp },
    },
];
const Spectra1DContextMenuOptions = [
    ...Spectra2DContextMenuOptions,
    {
        label: 'Export as jcamp',
        type: 'option',
        icon: (0, jsx_runtime_1.jsx)(fa_1.FaFileExport, {}),
        data: { id: SpectraContextMenuOptionsKeys.ExportAsJcamp },
    },
];
function SpectraTable(props) {
    const { data, activeSpectra, onChangeVisibility, onOpenSettingModal, onChangeActiveSpectrum, nucleus, } = props;
    const alert = (0, Alert_1.useAlert)();
    const dispatch = (0, DispatchContext_1.useDispatch)();
    const spectraPreferences = (0, usePanelPreferences_1.usePanelPreferences)('spectra', nucleus);
    const activeSpectraObj = getActiveSpectraAsObject(activeSpectra);
    const [exportedSpectrum, setExportedSpectrum] = (0, react_1.useState)();
    const COLUMNS = (0, react_1.useMemo)(() => ({
        visible: {
            id: 'hide-show-spectrum',
            Header: '',
            style: {
                width: '35px',
                maxWidth: '55px',
            },
            Cell: ({ row }) => {
                return ((0, jsx_runtime_1.jsx)(ShowHideSpectrumButton_1.default, { data: row.original, onChangeVisibility: onChangeVisibility }));
            },
        },
        name: {
            Header: '',
            style: columnStyle,
            accessor: (row) => row.info.name,
            Cell: ({ row }) => {
                return (0, jsx_runtime_1.jsx)(SpectrumName_1.SpectrumName, { data: row.original });
            },
        },
        solvent: {
            Header: '',
            style: columnStyle,
            accessor: (row) => row.info.solvent,
            Cell: ({ row }) => {
                const info = row.original.info;
                return (info?.solvent && ((0, jsx_runtime_1.jsx)("div", { 
                    // style={styles.info}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML: {
                        __html: formatValueAsHTML(info.solvent),
                    } })));
            },
        },
        color: {
            id: 'spectrum-actions',
            style: {
                width: '30px',
                maxWidth: '30px',
            },
            Cell: ({ row }) => {
                const { display, info: { dimension, isFid }, } = row.original;
                if (dimension === 2 && isFid) {
                    return (0, jsx_runtime_1.jsx)("div", {});
                }
                return ((0, jsx_runtime_1.jsx)(ColorIndicator_1.default, { display: display, dimension: dimension, onClick: (event) => onOpenSettingModal(event, row.original) }));
            },
        },
    }), [onChangeVisibility, onOpenSettingModal]);
    const { rawWriteWithType, cleanShouldFallback, shouldFallback, text } = (0, clipboardHooks_1.useClipboard)();
    const selectContextMenuHandler = (0, react_1.useCallback)((option, spectrum) => {
        const { id } = option.data;
        switch (id) {
            case SpectraContextMenuOptionsKeys.CopyToClipboard: {
                void (async () => {
                    const { data, info } = spectrum;
                    await rawWriteWithType(JSON.stringify({ data, info }, (_, value) => ArrayBuffer.isView(value) ? Array.from(value) : value, 2));
                    alert.success('Data copied to clipboard');
                })();
                break;
            }
            case SpectraContextMenuOptionsKeys.Delete: {
                setTimeout(() => {
                    dispatch({ type: 'DELETE_SPECTRA', payload: { id: spectrum.id } });
                }, 0);
                break;
            }
            case SpectraContextMenuOptionsKeys.ExportAsJcamp: {
                setExportedSpectrum(spectrum);
                break;
            }
            default: {
                break;
            }
        }
    }, [alert, dispatch, rawWriteWithType]);
    function handleActiveRow(row) {
        return activeSpectraObj?.[row?.original.id] || false;
    }
    const tableColumns = (0, react_1.useMemo)(() => {
        const columns = [];
        let index = 0;
        const visibleColumns = spectraPreferences.columns.filter((col) => col.visible);
        for (const col of visibleColumns) {
            const name = col?.name;
            const path = col?.jpath;
            if (name && COLUMNS[name]) {
                columns.push({
                    ...COLUMNS[name],
                    Header: () => (0, jsx_runtime_1.jsx)(ColumnHeader, { label: col.label, col: col }),
                    id: name,
                    style: name === 'name' && visibleColumns.length > 3
                        ? {
                            ...COLUMNS[name].style,
                            width: '50%',
                        }
                        : COLUMNS[name].style,
                });
            }
            else {
                columns.push({
                    Header: () => (0, jsx_runtime_1.jsx)(ColumnHeader, { label: col.label, col: col }),
                    accessor: (row) => (0, get_1.default)(row, path, ''),
                    id: `${index}`,
                    style: columnStyle,
                });
            }
            index++;
        }
        return columns;
    }, [COLUMNS, spectraPreferences.columns]);
    function handleSortEnd(data) {
        dispatch({
            type: 'ORDER_SPECTRA',
            payload: {
                data,
            },
        });
    }
    function handleRowStyle(data) {
        return {
            base: activeSpectraObj?.[data?.original.id] ? { opacity: 0.2 } : {},
            activated: { opacity: 1 },
        };
    }
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(ReactTable_1.default, { rowStyle: handleRowStyle, activeRow: handleActiveRow, data: data, columns: tableColumns, onClick: (e, data) => onChangeActiveSpectrum(e, data.original), enableVirtualScroll: true, approxItemHeight: 26, contextMenu: data.info && data.info.dimension === 1
                    ? Spectra1DContextMenuOptions
                    : Spectra2DContextMenuOptions, onContextMenuSelect: selectContextMenuHandler, onSortEnd: handleSortEnd, style: { 'table td': { paddingTop: 0, paddingBottom: 0 } } }), exportedSpectrum && ((0, jsx_runtime_1.jsx)(ExportAsJcampModal_1.default, { spectrum: exportedSpectrum, closeDialog: () => {
                    setExportedSpectrum(null);
                } })), (0, jsx_runtime_1.jsx)(clipboardComponents_1.ClipboardFallbackModal, { mode: shouldFallback, onDismiss: cleanShouldFallback, text: text, label: "Spectra" })] }));
}
exports.SpectraTable = SpectraTable;
const ColumnHeader = ({ label, col, }) => {
    const dispatch = (0, DispatchContext_1.useDispatch)();
    function selectHandler() {
        if (col?.jpath) {
            dispatch({
                type: 'RECOLOR_SPECTRA_COLOR',
                payload: { jpath: col?.jpath },
            });
        }
    }
    return ((0, jsx_runtime_1.jsx)(ui_1.DropdownMenu, { trigger: "contextMenu", options: options, onSelect: selectHandler, children: (0, jsx_runtime_1.jsx)("div", { style: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }, children: label }) }));
};
